import React from "react";
import { ArrowLeft } from "lucide-react";
import { finApi } from "@/lib/financeApi";
import { Chip, ErrorBox, INVOICE_CHIP, Loading, useLoad } from "@/components/nds/windows/finance/parts";
import InvoiceDetailsTab from "@/components/nds/windows/finance/InvoiceDetailsTab";
import InvoiceLinesTab from "@/components/nds/windows/finance/InvoiceLinesTab";
import InvoiceHistoryTab from "@/components/nds/windows/finance/InvoiceHistoryTab";
import { money } from "@/lib/financeApi";
import { CardDeck, CardsBack, useCardTabs } from "@/components/nds/TabCards";

const TABS = [
  { id: "details", label: "Details", Tab: InvoiceDetailsTab },
  { id: "lines", label: "Lines", Tab: InvoiceLinesTab },
  { id: "history", label: "History", Tab: InvoiceHistoryTab },
];

export default function FinInvoiceDetail({ invoiceId, onBack }) {
  const ct = useCardTabs("details");
  const tab = ct.tab;
  const setTab = ct.open;
  const [state, load] = useLoad(() => finApi.get(`/invoices/${invoiceId}`), [invoiceId]);
  const back = (
    <button type="button" onClick={onBack} data-testid="fin-inv-back" className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold">
      <ArrowLeft className="h-3 w-3" /> All invoices
    </button>
  );
  if (state.loading && !state.data) return <div className="p-5">{back}<Loading testid="fin-inv-detail-loading" /></div>;
  if (state.error) return <div className="p-5">{back}<ErrorBox testid="fin-inv-detail-error" error={state.error} onRetry={load} /></div>;
  const inv = state.data;
  const { Tab } = TABS.find((t) => t.id === tab);
  const [tone, label] = INVOICE_CHIP[inv.status];
  const cards = [
    { id: "details", label: "Details", tone: "financial", desc: "Vendor, dates, residents and invoice actions", stat: money(inv.total_amount || 0), note: label },
    { id: "lines", label: "Lines", tone: "data", desc: "Line items on this invoice", stat: (inv.lines || []).length, note: (inv.lines || []).length === 1 ? "line" : "lines" },
    { id: "history", label: "History", tone: "system", desc: "Every change made to this invoice", stat: (inv.history || []).length, note: (inv.history || []).length === 1 ? "event" : "events" },
  ];
  return (
    <div data-testid="fin-inv-detail" className="flex flex-col h-full">
      <div className="px-5 pt-4 space-y-3 border-b border-white/10">
        {back}
        <div className="flex flex-wrap items-center gap-3">
          <h3 data-testid="fin-inv-detail-title" className="text-lg text-nex-silver font-semibold">{inv.vendor_name} · {inv.invoice_number}</h3>
          <Chip tone={tone} data-testid="fin-inv-detail-status">{label}</Chip>
        </div>
        {ct.compact ? <div className="pb-3" /> : <div className="flex gap-4">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`fin-inv-tab-${t.id}`} aria-pressed={tab === t.id}
              className={`pb-2 font-mono text-[10px] tracking-[0.2em] uppercase border-b-2 transition-colors ${tab === t.id ? "border-nex-gold text-nex-gold" : "border-transparent text-nex-muted hover:text-nex-silver"}`}>
              {t.label}
            </button>
          ))}
        </div>}
      </div>
      <div ref={ct.ref} className="flex-1 overflow-y-auto">
        {ct.deck ? <div className="p-5"><CardDeck testid="fin-inv-cards" items={cards} onOpen={setTab} /></div>
          : <>{ct.compact && <CardsBack testid="fin-inv-cards-back" className="mx-5 mt-4" label={TABS.find((t) => t.id === tab).label} onClick={ct.close} />}<Tab inv={inv} reload={load} onDeleted={onBack} /></>}
      </div>
    </div>
  );
}
